import { useState } from 'react';
import { Card, CardContent, Typography, Box, Grid, CircularProgress } from '@mui/material';
import TokenIcon from '@mui/icons-material/Bolt';
import CallIcon from '@mui/icons-material/Sync';
import ForumIcon from '@mui/icons-material/Forum';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useAgentUsageStats } from '../../hooks/useAgentUsageStats';
import { useTopQuestionsAsked } from '../../hooks/useTopQuestionsAsked';
import { AgentUsageDetailKind } from '../../hooks/useAgentUsageDetail';
import { formatDuration, formatToolName, truncate } from '../../utils/agentUsageFormat';
import AgentUsageDetailModal from './AgentUsageDetailModal';
import TopBarChart from './TopBarChart';

const QUESTION_BAR_COLOR = '#2E7D32';
const USER_BAR_COLOR = '#F57C00';

/** Wraps a category label onto up to 2 lines instead of rotating it, so long tool names stay horizontal and readable. */
const wrapLabel = (label: string, maxCharsPerLine = 14): string[] => {
  const words = label.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > maxCharsPerLine && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines.slice(0, 2);
};

const WrappedAxisTick = ({ x, y, payload }: any) => {
  const lines = wrapLabel(payload.value);
  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, i) => (
        <text key={line} x={0} y={0} dy={12 + i * 12} textAnchor="middle" fontSize={10} fill="#64748b">
          {line}
        </text>
      ))}
    </g>
  );
};

const StatTile = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
  <Card sx={{ flex: 1, p: 2 }}>
    <CardContent sx={{ p: '0 !important' }}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        {icon}
        <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
      </Box>
      <Typography variant="h5" fontWeight="bold">{value}</Typography>
    </CardContent>
  </Card>
);

interface DetailModalState {
  kind: AgentUsageDetailKind;
  identifier: string;
  label: string;
}

const AgentUsageLogCard = () => {
  const [detailModal, setDetailModal] = useState<DetailModalState | null>(null);

  const { data, loading, error } = useAgentUsageStats();
  const { data: topQuestions, loading: questionsLoading, error: questionsError } = useTopQuestionsAsked();

  if (loading) {
    return (
      <Card sx={{ p: 2, height: '100%' }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card sx={{ p: 2, height: '100%' }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={2}>Agent Usage</Typography>
          <Typography color="error">{error || 'No data available'}</Typography>
        </CardContent>
      </Card>
    );
  }

  const { totals, tokensOverTime, byTool, topUsers, sessions } = data;
  const toolChartData = byTool.map((t) => ({ ...t, label: formatToolName(t.tool) }));
  const topQuestionsData = (topQuestions ?? []).map((q) => ({ key: q.normalizedMessage, name: truncate(q.question, 50), count: q.count }));
  const topUsersData = topUsers.map((u) => ({ key: u.userId, name: u.userName, count: u.calls }));

  return (
    <Card sx={{ p: 2, height: '100%' }}>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" mb={3}>Agent Usage</Typography>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile icon={<TokenIcon color="warning" />} label="Total Tokens" value={totals.totalTokens.toLocaleString()} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile icon={<CallIcon color="primary" />} label="API Calls" value={totals.calls.toLocaleString()} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile icon={<ForumIcon color="secondary" />} label="Sessions" value={sessions.count.toLocaleString()} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile icon={<AccessTimeIcon color="success" />} label="Total Time Used" value={formatDuration(totals.totalLatencyMs)} />
          </Grid>
        </Grid>

        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="baseline" gap={1} mb={1}>
              <Typography variant="subtitle2" fontWeight="bold">Tokens Over Time</Typography>
              <Typography variant="caption" color="text.secondary">(full history — not limited by the date filter)</Typography>
            </Box>
            {tokensOverTime.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={tokensOverTime} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8eaf0" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={{ stroke: '#cbd5e1' }}
                    interval="preserveStartEnd"
                    minTickGap={24}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={50} />
                  <Tooltip formatter={(value: number) => value.toLocaleString()} />
                  <Line type="monotone" dataKey="totalTokens" name="Tokens" stroke="#1976D2" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary">No agent activity recorded yet.</Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>API Calls</Typography>
            {toolChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={toolChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8eaf0" vertical={false} />
                  <XAxis dataKey="label" tick={<WrappedAxisTick />} height={36} interval={0} tickLine={false} axisLine={{ stroke: '#cbd5e1' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} allowDecimals={false} width={40} />
                  <Tooltip />
                  <Bar
                    dataKey="calls"
                    name="Calls"
                    fill="#C62828"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={40}
                    onClick={(entry: any) => setDetailModal({ kind: 'tool', identifier: entry.tool, label: entry.label })}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary">No agent activity in this range.</Typography>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={6}>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>Top 5 Users</Typography>
            <TopBarChart
              data={topUsersData}
              color={USER_BAR_COLOR}
              tooltipSuffix="calls"
              emptyMessage="No agent activity in this range."
              onEntryClick={(entry) => setDetailModal({ kind: 'user', identifier: entry.key, label: entry.name })}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>Top 5 Questions Asked</Typography>
            {questionsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={180}>
                <CircularProgress size={28} />
              </Box>
            ) : questionsError ? (
              <Typography color="error">{questionsError}</Typography>
            ) : (
              <TopBarChart
                data={topQuestionsData}
                color={QUESTION_BAR_COLOR}
                tooltipSuffix="asked"
                emptyMessage="No questions asked in this range."
                onEntryClick={(entry) => setDetailModal({ kind: 'question', identifier: entry.key, label: entry.name })}
              />
            )}
          </Grid>
        </Grid>
      </CardContent>

      <AgentUsageDetailModal
        open={detailModal !== null}
        onClose={() => setDetailModal(null)}
        kind={detailModal?.kind ?? null}
        identifier={detailModal?.identifier ?? null}
        label={detailModal?.label ?? ''}
      />
    </Card>
  );
};

export default AgentUsageLogCard;
